import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, X, History, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  usePatientDiseaseHistory,
  useDeleteDiseaseHistory,
  useExcludeFromPrescription,
  useDeactivateDiseaseHistory,
  useInitializeAppointmentDiseaseDraft,
} from "@/hooks/useConsultation";
import { AddDiseaseHistoryDialog } from "@/components/consultation/AddDiseaseHistoryDialog";
import type { PatientDiseaseHistory } from "@/types/appointments";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";

interface DiagnosisCardProps {
  appointmentId: string;
  patientId: string;
  isCompleted?: boolean;
}

const isActive = (h: PatientDiseaseHistory) => h.activity_status !== "inactive";
const isPastDraft = (h: PatientDiseaseHistory) => h.draft_context === "past";
const isAppointmentAddedDraft = (h: PatientDiseaseHistory) =>
  h.draft_context === "in_progress";

export const DiagnosisCard = ({
  appointmentId,
  patientId,
  isCompleted = false,
}: DiagnosisCardProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isNutritionist } = useAuthStore();

  const source: "meal_plan" | "prescription" = isNutritionist()
    ? "meal_plan"
    : "prescription";

  const { mutate: initializeDraft } = useInitializeAppointmentDiseaseDraft();
  const initializedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${appointmentId}:${source}`;
    if (!appointmentId || initializedRef.current === key) return;
    initializedRef.current = key;
    initializeDraft({ appointmentId, source });
  }, [appointmentId, source, initializeDraft]);

  const { data: diseaseHistory = [] } = usePatientDiseaseHistory(
    appointmentId,
    source
  );
  const deleteMutation = useDeleteDiseaseHistory();
  // Past rows are copied from committed patient history. Prescription past rows
  // use the include/exclude endpoints; appointment-added rows can be deleted.
  const excludeMutation = useExcludeFromPrescription(appointmentId);
  const deactivateMutation = useDeactivateDiseaseHistory(
    appointmentId,
    patientId
  );

  const handleRemove = async (history: PatientDiseaseHistory) => {
    try {
      if (isPastDraft(history)) {
        if (source === "prescription") {
          await excludeMutation.mutateAsync(history.id);
        } else {
          await deactivateMutation.mutateAsync(history.id);
        }
      } else {
        await deleteMutation.mutateAsync({
          id: appointmentId,
          rowId: history.id,
          source,
        });
      }
      toast({
        title: "Success",
        description: "Diagnosis removed successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove diagnosis",
        variant: "destructive",
      });
    }
  };

  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  // Pre-Existing: active + past + confirmed (catalog only).
  // New Diagnosis: active + in_progress + (confirmed | provisional) (catalog only).
  // Self-reported and custom-unresolved rows are surfaced/managed in the
  // PreExistingConditionsWidget, not here.
  const preExisting = diseaseHistory.filter(
    (d) =>
      isActive(d) &&
      isPastDraft(d) &&
      d.disease_status === "confirmed" &&
      !d.is_custom &&
      d.disease != null
  );
  const newDiagnoses = diseaseHistory.filter(
    (d) =>
      isActive(d) &&
      isAppointmentAddedDraft(d) &&
      (d.disease_status === "confirmed" ||
        d.disease_status === "provisional") &&
      !d.is_custom &&
      d.disease != null
  );

  const renderDiagnosisItem = (item: PatientDiseaseHistory) => {
    const isProvisional = item.disease_status === "provisional";

    return (
      <div
        key={item.id}
        className={cn(
          "flex items-center justify-between p-2 rounded-lg border group transition-colors",
          isProvisional
            ? "border-warning/30 bg-warning/10 text-warning"
            : "bg-muted/40 border-border/60 text-foreground"
        )}
      >
        <div className="flex items-center gap-2">
          {isProvisional && (
            <span className="flex items-center justify-center size-5 rounded-full bg-warning/15 dark:bg-warning/30 z-hint font-bold">
              ?
            </span>
          )}
          <span className="z-body-md">{item.disease?.display_name}</span>
        </div>

        {!isCompleted && (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 rounded-xl opacity-0 group-hover:opacity-100 transition-all",
              "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            )}
            onClick={() => handleRemove(item)}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm h-full flex flex-col">
        <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60 shrink-0">
          <div className="flex items-center gap-2">
            <CardTitle className="z-h3 text-foreground">Diagnosis</CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-5 flex-1 overflow-y-auto min-h-0">
          {/* Pre-Existing Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <History className="size-4" />
                <h4 className="z-eyebrow">Pre-Existing</h4>
              </div>
            </div>

            {preExisting.length > 0 ? (
              <div className="space-y-2">
                {preExisting.map(renderDiagnosisItem)}
              </div>
            ) : (
              <div
                className={cn(
                  "p-4 border border-dashed border-border/60 rounded-xl bg-muted/30 text-center transition-all"
                )}
              >
                <p className="z-caption text-muted-foreground">
                  No pre-existing diagnosis
                </p>
              </div>
            )}
          </div>

          {/* New Diagnosis Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                {/* theme-drift: decorative */}
                <div className="size-4 rounded-full bg-sky-100 dark:bg-sky-950/40 flex items-center justify-center text-sky-700 dark:text-sky-300 z-hint font-bold">
                  N
                </div>
                <h4 className="z-eyebrow">New Diagnosis</h4>
              </div>
              {!isCompleted && (
                <Button
                  size="icon"
                  className="h-9 w-9 rounded-xl"
                  onClick={handleAddClick}
                >
                  <Plus className="size-4" />
                </Button>
              )}
            </div>

            {newDiagnoses.length > 0 ? (
              <div className="space-y-2">
                {newDiagnoses.map(renderDiagnosisItem)}
              </div>
            ) : (
              <div
                className={cn(
                  "p-4 border border-dashed border-border/60 rounded-xl bg-muted/30 text-center transition-all",
                  !isCompleted &&
                    "cursor-pointer hover:bg-muted/50 hover:border-primary/30"
                )}
                onClick={() => !isCompleted && handleAddClick()}
              >
                <p className="z-caption text-muted-foreground">
                  Click to add new diagnosis
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AddDiseaseHistoryDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        patientId={patientId}
        appointmentId={appointmentId}
        defaultSource={source}
        lockSource={true}
        allowProvisionalStatus={source === "prescription"}
      />
    </>
  );
};
